<script lang="ts">
  import { Modal, Button, FormSelect } from '../common';

  let {
    isOpen = $bindable(false),
    categoryToDelete = '',
    availableCategories = [],
    onConfirm = (replacementCategory: string) => {},
    onCancel = () => {}
  }: {
    isOpen: boolean;
    categoryToDelete: string;
    availableCategories: string[];
    onConfirm: (replacementCategory: string) => void;
    onCancel: () => void;
  } = $props();

  let selectedReplacement = $state('Uncategorized');

  // Filter out the category being deleted from the list
  const filteredCategories = $derived(
    availableCategories.filter(c => c !== categoryToDelete)
  );

  function handleConfirm() {
    onConfirm(selectedReplacement);
    isOpen = false;
  }

  function handleCancel() {
    onCancel();
    isOpen = false;
  }

  // Reset selection when modal opens
  $effect(() => {
    if (isOpen) {
      selectedReplacement = 'Uncategorized';
    }
  });
</script>

<Modal {isOpen} title="Delete Category" onClose={handleCancel} size="md">
  {#snippet children()}
    <div class="modal-content">
      <div class="warning-section">
        <div class="warning-icon">⚠️</div>
        <div class="warning-text">
          <h3>You are about to delete "{categoryToDelete}"</h3>
          <p>This action will:</p>
          <ul>
            <li>Remove this category from the dropdown list</li>
            <li>Update all transactions currently using this category</li>
            <li>Update all categorization rules using this category</li>
            <li><strong>Break auto-categorization</strong> - Future transactions that would have been categorized as "{categoryToDelete}" will use the replacement category instead</li>
          </ul>
        </div>
      </div>

      <div class="selection-section">
        <FormSelect
          label="Replace with"
          bind:value={selectedReplacement}
          options={filteredCategories}
        />
        <p class="help-text">
          All existing transactions and rules will be updated to use this category.
          Future auto-categorization will also map to this category.
        </p>
      </div>
    </div>
  {/snippet}

  {#snippet footer()}
    <Button variant="secondary" onclick={handleCancel}>Cancel</Button>
    <Button variant="danger" onclick={handleConfirm}>Delete Category</Button>
  {/snippet}
</Modal>

<style lang="scss">
  .modal-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-2xl);
  }

  .warning-section {
    display: flex;
    gap: var(--spacing-lg);
    padding: var(--spacing-lg);
    background: var(--color-danger-light);
    border: 2px solid var(--color-danger-border);
    border-radius: var(--radius-md);

    .warning-icon {
      font-size: var(--font-size-3xl);
      flex-shrink: 0;
    }

    .warning-text {
      flex: 1;

      h3 {
        margin: 0 0 var(--spacing-sm) 0;
        font-size: var(--font-size-lg);
        font-weight: var(--font-weight-semibold);
        color: var(--color-text-primary);
      }

      p {
        margin: 0 0 var(--spacing-xs) 0;
        color: var(--color-text-secondary);
        font-size: var(--font-size-sm);
      }

      ul {
        margin: 0;
        padding-left: var(--spacing-xl);
        color: var(--color-text-secondary);
        font-size: var(--font-size-sm);

        li {
          margin-bottom: var(--spacing-xs);

          strong {
            color: var(--color-danger-dark);
            font-weight: var(--font-weight-semibold);
          }
        }
      }
    }
  }

  .selection-section {
    .help-text {
      margin-top: var(--spacing-sm);
      font-size: var(--font-size-xs);
      color: var(--color-text-muted);
      font-style: italic;
    }
  }
</style>
